import React, { useState } from 'react';

/**
 * Custom hook for sorting data.
 * @param {Array} data - The data to be sorted.
 * @param {Object} defaultSortConfig - The default sorting configuration.
 * @param {Object} sortValueFns - Optional map of key → (value, row) => comparable value for columns
 *   whose sort value differs from the raw data property (e.g. supplierId → supplierName).
 * @returns {Object} - An object containing the sorted data, a function to update the criteria and the sort criteria.
 **/

const EMPTY_SORT_VALUE_FNS = {};

export function useSortData(data, defaultSortConfig = { key: null, direction: 'ascending' }, sortValueFns = EMPTY_SORT_VALUE_FNS) {
    const [sortConfig, setSortConfig] = useState(defaultSortConfig);

    const sortBy = (key) => {
        let direction = 'ascending';
        if (
            sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'ascending'
        ) {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = React.useMemo(() => {
        if (data === null) return data;

        const compareItems = (a, b) => {
            const sortKey = sortConfig.key;

            // Get the value to sort by, using the sortValueFns if provided,
            // otherwise falling back to the raw value
            const hasFn = typeof sortValueFns[sortKey] === 'function';
            const aVal = hasFn ? sortValueFns[sortKey](a[sortKey], a) : a[sortKey];
            const bVal = hasFn ? sortValueFns[sortKey](b[sortKey], b) : b[sortKey];

            const aHasValue = aVal !== undefined && aVal !== null;
            const bHasValue = bVal !== undefined && bVal !== null;
            const neitherItemHasValue = !aHasValue && !bHasValue;

            if (neitherItemHasValue) {

                if (a.items) {
                    // If neither item has the key, and it's a parent item, we sort by description as a fallback
                    const aDescription = a.description ?? '';
                    const bDescription = b.description ?? '';

                    const aDescriptionLower = aDescription.toLowerCase();
                    const bDescriptionLower = bDescription.toLowerCase();

                    if (aDescriptionLower < bDescriptionLower) {
                        return -1;
                    } else if (aDescriptionLower > bDescriptionLower) {
                        return 1;
                    }
                }

                return 0; // If neither item has the key and they're not parent items, consider them equal
            } else if (!aHasValue) {
                // If a doesn't have the key, it should come before b (or after b if descending)
                return sortConfig.direction === 'ascending' ? -1 : 1;
            } else if (!bHasValue) {
                // If b doesn't have the key, it should come before a (or after a if descending)
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }

            // for string values, sort case-insensitively
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                const aLower = aVal.toLowerCase();
                const bLower = bVal.toLowerCase();

                if (aLower < bLower) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aLower > bLower) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            }

            // Fallback for non-string values (e.g. numbers, dates)
            if (aVal < bVal) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aVal > bVal) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        };

        const sortItems = (items) => {
            const sorted = [...items].sort(compareItems);
            return sorted.map(item => {
                if (item.items && Array.isArray(item.items)) {
                    return { ...item, items: sortItems(item.items) };
                }
                return item;
            });
        };

        if (sortConfig === null || sortConfig.key === null) return data;

        return sortItems(data);
    }, [data, sortConfig, sortValueFns]);

    return { sortedData, sortBy, sortConfig };
}